import React, { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Brand
    appName: 'Mizan',
    appTagline: 'Personal finance, modular.',

    // Navigation
    dashboard: 'Dashboard',
    reports: 'Reports',
    receiptHistory: 'Receipt History',
    uploadReceipt: 'Upload Receipt',
    login: 'Login',
    signup: 'Sign Up',
    logout: 'Logout',
    greeting: 'Hello, {{name}}',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    apiKey: 'API Key',

    // Dashboard empty state
    dashboardEmptyTitle: 'No modules enabled',
    dashboardEmptyDescription: 'Enable a module from your settings to see widgets here.',

    // Onboarding modal
    onboardingTitle: 'Welcome to Mizan',
    onboardingIntro: 'The receipt tracker has grown into a modular personal finance app. Receipts are still here — and there\'s more.',
    onboardingReceiptsDesc: 'Upload receipts, auto-extract items, and analyze spending — exactly as before.',
    onboardingBudgetDesc: 'Import Turkish credit card statements, auto-categorize transactions, and track installment debt.',
    onboardingClosingNote: 'Pick a module from the navbar to get started. You won\'t see this message again.',
    onboardingDismiss: 'Got it',

    // Common actions
    viewAll: 'View all',
    insights: 'Insights',
    apiSection: 'API',

    // API Key page
    'api.quickStart': 'Quick Start',
    'api.baseUrl': 'Base URL',
    'api.authentication': 'Authentication',
    'api.authDescPre': 'Add an',
    'api.authDescPost': 'header to every request.',
    'api.endpoints': 'Endpoints',
    'api.colMethod': 'Method',
    'api.colPath': 'Path',
    'api.colDesc': 'Description',
    'api.uploadCurl': 'Upload a receipt (curl)',
    'api.epListReceipts': 'List all receipts (paginated)',
    'api.epGetReceipt': 'Get a single receipt',
    'api.epGetItems': 'Get line items for a receipt',
    'api.epProcess': 'Upload & AI-process a receipt image (multipart/form-data, field: file)',
    'api.epDashStats': 'Spending stats used by the dashboard',
    'api.iosShortcuts': 'iOS Shortcuts',
    'api.iosIntro': 'Use the Shortcuts app on iPhone to automate receipt uploads or fetch your spending data with a tap or Siri command.',
    'api.keyLabel': 'Key:',
    'api.valueLabel': 'Value: your key',
    'api.r1Title': 'Fetch latest receipts',
    'api.r1Step1': 'Open Shortcuts → tap + → Add Action',
    'api.r1Step2': 'Search Get Contents of URL and add it',
    'api.r1Step3': 'Set URL to:',
    'api.r1Step4': 'Expand Headers → Add header:',
    'api.r1Step5': 'Add Get Dictionary from Input (optional — to pick specific fields)',
    'api.r1Step6': 'Add Show Result or Quick Look to preview the response',
    'api.r1Note': 'Tip: name the shortcut "My Receipts" and trigger it with "Hey Siri, My Receipts".',
    'api.r2Title': 'Upload a receipt photo',
    'api.r2Step1': 'Add action Select Photos (limit to 1 photo)',
    'api.r2Step2': 'Add Get Contents of URL',
    'api.r2Step3': 'Set URL to:',
    'api.r2Step4Pre': 'Set Method to',
    'api.r2Step5a': 'Set Request Body → Form',
    'api.r2Step5bPre': 'Add field: Key',
    'api.r2Step5bPost': ', Value → Photos (the variable from step 1)',
    'api.r2Step6': 'Add Header:',
    'api.r2Step6b': ', Value: your key',
    'api.r2Step7': 'Add Show Result — the AI-extracted receipt data appears instantly',
    'api.r2Note': 'Tip: add this shortcut to your home screen for one-tap receipt capture after shopping.',

    // Modules
    'module.receipts': 'Receipts',
    'module.budget': 'Budget',

    // Budget module
    'budget.statements': 'Statements',
    'budget.statement': 'Statement',
    'budget.uploadStatement': 'Upload Statement',
    'budget.selectPdf': 'Select PDF',
    'budget.pdfHint': 'Upload a Turkish credit card PDF statement. Max 10MB.',
    'budget.processing': 'Processing...',
    'budget.uploadSuccess': 'Imported {{count}} transactions from {{bank}}.',
    'budget.uploadError': 'Statement upload failed.',
    'budget.uploadErrorPdfOnly': 'Only PDF files are supported.',
    'budget.uploadErrorTooLarge': 'PDF exceeds 10 MB.',
    'budget.uploadErrorDuplicate': 'This statement was already imported.',
    'budget.uploadErrorParse': 'Could not parse this statement.',
    'budget.uploadErrorParse.PDF_UNREADABLE': 'The file could not be opened as a PDF. It may be corrupt or password-protected. Download the statement again from your bank and retry.',
    'budget.uploadErrorParse.NO_TEXT': 'The PDF contains no readable text. Scanned or image-only statements are not supported yet. Download the original PDF statement from your bank\'s website or app.',
    'budget.uploadErrorParse.LLM_RATE_LIMITED': 'The parsing service is busy right now (rate limit reached). Nothing is wrong with your file. Wait a minute and try again.',
    'budget.uploadErrorParse.LLM_UNAVAILABLE': 'The parsing service is temporarily unavailable. Nothing is wrong with your file. Try again in a few minutes.',
    'budget.uploadErrorParse.LLM_AUTH': 'The parsing service rejected the server\'s credentials. This is a server configuration problem. Please report it.',
    'budget.uploadErrorParse.INVALID_RESPONSE': 'The statement text was read, but its contents could not be turned into transactions. This bank\'s layout may not be supported yet. Please report it with the bank name.',
    'budget.uploadErrorParse.UNKNOWN': 'An unexpected error occurred while parsing. Please try again or report the problem.',
    'budget.uploadErrorDetail': 'Technical detail',
    'budget.viewStatement': 'View statement',
    'budget.viewDetails': 'View details',
    'budget.noStatementsYet': 'No statements imported yet.',
    'budget.period': 'Period',
    'budget.totalDue': 'Total due',
    'budget.minimumPayment': 'Minimum payment',
    'budget.dueDate': 'Due date',
    'budget.confirmDeleteStatement': 'Delete this statement and all its transactions?',
    'budget.transactions': 'Transactions',
    'budget.merchant': 'Merchant',
    'budget.amount': 'Amount',
    'budget.installment': 'Installment',
    'budget.installments': 'Installments',
    'budget.category': 'Category',
    'budget.uncategorized': 'Uncategorized',
    'budget.edited': 'Edited',
    'budget.amountNeedsReview': 'Check amount',
    'budget.amountNeedsReviewHint': 'This amount could not be confirmed against the statement. Compare it with your PDF.',
    'budget.amountsNeedReview': '{{count}} amount(s) could not be confirmed against the statement. Please compare the flagged rows with your PDF, then correct or confirm them.',
    'budget.correctAmount': 'Correct',
    'budget.confirmAmount': 'Amount is correct',
    'budget.amountInputLabel': 'Amount as printed on the statement',
    'budget.amountInvalid': 'Enter a non-zero amount with at most two decimals, e.g. 42.333,00',
    'budget.amountCorrected': 'Amount updated',
    'budget.amountConfirmed': 'Amount confirmed',
    'budget.amountUpdateFailed': 'Could not update the amount. Please try again.',
    'budget.linkReceipt': 'Link receipt',
    'budget.noReceiptSuggestions': 'No receipt candidates found nearby.',
    'budget.link': 'Link',

    'budget.cards': 'Cards',
    'budget.card': 'Card',
    'budget.addCard': 'Add card',
    'budget.bank': 'Bank',
    'budget.cardName': 'Card name',
    'budget.last4': 'Last 4',
    'budget.color': 'Color',
    'budget.creditLimit': 'Credit limit',
    'budget.add': 'Add',
    'budget.noCardsYet': 'No cards yet.',
    'budget.confirmDeleteCard': 'Delete this card?',

    'budget.categoriesAndRules': 'Categories and rules',
    'budget.categories': 'Categories',
    'budget.rules': 'Rules',
    'budget.addCategory': 'Add category',
    'budget.addRule': 'Add rule',
    'budget.displayNameTr': 'Display name (TR)',
    'budget.displayNameEn': 'Display name (EN)',
    'budget.confirmDeleteCategory': 'Delete this category?',
    'budget.matchType': 'Match type',
    'budget.pattern': 'Pattern',
    'budget.priority': 'Priority',
    'budget.pickCategory': 'Pick category',
    'budget.system': 'System',

    'budget.addInstallmentManual': 'Add installment',
    'budget.pickCardOptional': 'Card (optional)',
    'budget.totalAmount': 'Total amount',
    'budget.monthlyAmount': 'Monthly',
    'budget.count': 'Count',
    'budget.firstDueMonth': 'First due month',
    'budget.nextDue': 'Next due',
    'budget.progress': 'Progress',
    'budget.open': 'Open',
    'budget.completed': 'Completed',
    'budget.all': 'All',
    'budget.markCompleted': 'Mark completed',
    'budget.reopen': 'Reopen',
    'budget.confirmDeleteInstallment': 'Delete this installment?',
    'budget.noInstallments': 'No installments.',
    'budget.unconfirmedMonths': '{{count}} month(s) not confirmed by a statement',
    'budget.totalDueForMonth': 'Total due',
    'budget.monthlyContribution': 'This month',
    'budget.gridView': 'Grid',
    'budget.listView': 'List',
    'budget.viewMode': 'View mode',

    'budget.findDuplicates': 'Find duplicates',
    'budget.duplicatesModalTitle': 'Duplicate installments',
    'budget.duplicatesScanning': 'Scanning for duplicates…',
    'budget.duplicatesNoneFound': 'No duplicate installments detected.',
    'budget.duplicatesFound': '{{count}} cluster(s) found, covering {{rows}} row(s).',
    'budget.duplicatesWinner': 'Keep',
    'budget.duplicatesLoser': 'Merge & delete',
    'budget.duplicatesReason': 'Winner picked because:',
    'budget.duplicatesReasonNotes': 'has manual notes',
    'budget.duplicatesReasonProgress': 'most paid installments',
    'budget.duplicatesReasonEarliest': 'oldest record',
    'budget.duplicatesApply': 'Apply merges',
    'budget.duplicatesApplying': 'Merging…',
    'budget.duplicatesConfirm': 'This will permanently delete the loser rows and repoint their transactions onto the winner. Continue?',
    'budget.duplicatesAppliedSummary': 'Merged {{clusters}} cluster(s): deleted {{rows}} installment(s), repointed {{txns}} transaction(s).',

    'budget.reportDebtForecast': 'Debt forecast',
    'budget.sixMonths': '6 months',
    'budget.twelveMonths': '12 months',
    'budget.twentyFourMonths': '24 months',
    'budget.clickToViewInstallments': 'Click to view installments',
    'budget.allMonths': 'All months',
    'budget.noInstallmentsForMonth': 'No installments due this month.',
    'budget.toastCompleted': 'Marked as completed',
    'budget.toastReopened': 'Installment reopened',
    'budget.toastDeleted': 'Installment deleted',
    'budget.total': 'Total',
    'budget.shareOfTotal': 'Share',

    'budget.dashboardTitle': 'Budget',
    'budget.widgetNextMonthDue': 'Installments due next month',
    'budget.widgetOpenInstallments': 'Open installments',

    'budget.widgetMonthSpend': 'Spent this month',
    'budget.widgetMonthSpendHint': 'Cards and receipts, each purchase once',

    'budget.reportSpendingByCategory': 'Spending by category',
    'budget.reportSpendingByStore': 'Spending by store',
    'budget.spendingTotal': 'Total spent',
    'budget.spendingMonthlyAverage': '{{amount}} per month on average',
    'budget.spendingByCard': 'Paid by card',
    'budget.spendingCardHint': '{{count}} charges, {{matched}} with a receipt',
    'budget.spendingReceiptOnly': 'Receipts without a card charge',
    'budget.spendingReceiptOnlyHint': '{{count}} receipts: cash, debit, or a statement not uploaded yet',
    'budget.spendingEmpty': 'No spending in this period. Upload a statement or a receipt to get started.',
    'budget.spendingCategoryBreakdown': 'Categories',
    'budget.spendingShare': '{{percent}}% of spending · {{count}} purchases',
    'budget.spendingStoreMeta': '{{count}} purchases · {{receipts}} with a receipt · {{percent}}% of spending',
    'budget.spendingSearchStores': 'Search stores',

    'budget.receipt': 'Receipt',
    'budget.receiptLinked': 'Receipt linked',
    'budget.unlinkReceipt': 'Unlink receipt',
    'budget.linkReceiptHint': 'Pick the receipt for this charge. A linked purchase is counted once in reports.',
    'budget.unknownStore': 'Unknown store',
    'budget.matchScore': 'Match score',
    'budget.receiptsLinkedCount': '{{count}} with a receipt',
    'budget.matchReceipts': 'Match receipts',
    'budget.matchingReceipts': 'Matching…',
    'budget.matchReceiptsDone': 'Linked {{count}} receipt(s) to card charges.',
    'budget.matchReceiptsNone': 'No new matches found. You can link receipts by hand.',

    'receipts.payment': 'Payment',
    'receipts.card': 'Card',
    'receipts.paidByCard': 'Paid by card',
    'receipts.viewStatement': 'View statement',

    // Auth
    username: 'Username',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    loginButton: 'Login',
    signupButton: 'Sign Up',
    
    // Dashboard
    welcomeMessage: 'Welcome to Receipt Analysis',
    totalSpending: 'Total Spending',
    monthlyOverview: 'Monthly Overview',
    totalReceipts: 'Total Receipts',
    spendingLast30Days: 'Spending in Last 30 Days',
    totalAmount: 'Total Amount',
    weeklySummary: 'Weekly Summary',
    totalSpendingLast7Days: 'Total Spending (Last 7 Days)',
    receiptsUploadedLast7Days: 'Receipts Uploaded (Last 7 Days)',
    recentStores: 'Recent Stores',
    
    // Reports
    'reports.allReports': 'All reports',
    'reports.selectReport': 'Select a report',
    'reports.emptyTitle': 'Pick a report to get started',
    'reports.emptyDescription': 'Choose any report from the list to see the breakdown.',
    'reports.jumpToReports': 'Jump to reports',
    'scope.mine': 'Mine',
    'scope.family': 'Family',
    'scope.label': 'Scope',
    viewDetails: 'View Details',
    receiptCount: '{{count}} receipts',
    backToGroups: 'Back to Groups',
    reset: 'Reset',
    selectStartDate: 'Select Start Date',
    selectEndDate: 'Select End Date',
    accountantExport: 'Accountant Export',
    accountantExportDescription: 'Download monthly receipt data as an Excel file for your accountant.',
    accountantExportButton: 'Download Excel',
    accountantExportDownloading: 'Downloading...',
    accountantExportError: 'Failed to download the report. Please try again.',
    selectYear: 'Year',
    selectMonth: 'Month',

    // Receipt Upload
    selectFile: 'Select File',
    upload: 'Upload',
    uploadSuccess: 'Receipt uploaded successfully',
    uploadError: 'Error uploading receipt',
    processing: 'Processing...',
    supportedFormats: 'Supported formats: JPG, PNG, GIF',
    selectReceiptImage: 'Select Receipt Image',
    errorHeading: 'Upload Error',
    errorFileTooLarge: 'File is too large. Maximum size allowed is {{maxSize}}',
    errorUnsupportedFormat: 'Unsupported file format. Please use JPG, PNG, or PDF files only',
    errorDuplicateReceipt: 'This receipt has already been uploaded',
    errorInvalidFormat: 'Unable to process the receipt. Please ensure the image is clear and contains readable text',
    errorProcessing: 'An error occurred while processing the receipt',
    errorProcessingHelp: 'Please try again or contact support if the issue persists',
    'receipts.uploadError.LLM_RATE_LIMITED': 'The receipt reading service is busy right now. Nothing is wrong with your photo. Wait a minute and try again.',
    'receipts.uploadError.LLM_UNAVAILABLE': 'The receipt reading service is temporarily unavailable. Nothing is wrong with your photo. Try again in a few minutes.',
    'receipts.uploadError.LLM_AUTH': 'The receipt reading service rejected the server\'s credentials. This is a server configuration problem. Please report it.',
    'receipts.retryUpload': 'Try again',
    
    // Receipt History
    date: 'Date',
    store: 'Store',
    total: 'Total',
    items: 'Items',
    searchReceipts: 'Search receipts...',
    purchasedProducts: 'Purchased Products',
    searchProducts: 'Search products...',
    actions: 'Actions',
    viewReceipt: 'View Receipt',
    product: 'Product',
    quantity: 'Quantity',
    unitPrice: 'Unit Price',
    price: "Price",
    totalPrice: 'Total Price',
    receipt: 'Receipt',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    deleting: 'Deleting',
    confirmDeletion: 'Confirm Deletion',
    confirmDeleteMessage: 'Are you sure you want to delete this receipt?',
    actionCannotBeUndone: 'This action cannot be undone.',
    deleteSuccess: 'Receipt deleted successfully.',
    deleteError: 'Failed to delete receipt. Please try again.',
    
    // Common
    loading: 'Loading...',
    error: 'An error occurred',
    noData: 'No data available',
    
    // API Key Management
    apiKeyManagement: 'API Key Management',
    yourApiKey: 'Your API Key',
    keepSecure: 'Keep this key secure. Do not share it with others.',
    noApiKey: 'No API key available',
    copy: 'Copy',
    apiKeyUsage: 'This API key can be used to authenticate requests to our API endpoints. Include it in the X-API-Key header of your requests.',
    fetchError: 'Failed to fetch API key. Please try again later.',

    // Bug Report Modal
    bugReportTitle: '🐞 Report a Bug',
    bugReportDescription: 'Description',
    bugReportDescriptionPlaceholder: 'Please describe the bug or suggest an improvement.',
    bugReportContactInfo: 'Contact Info',
    bugReportContactInfoPlaceholder: 'Email or other contact (optional, for follow-up)',
    bugReportExceptionDetails: 'Exception Details',
    bugReportExceptionDetailsDev: '(for developers)',
    bugReportBrowserInfo: 'Browser Info',
    bugReportBrowserInfoAuto: '(auto-captured)',
    bugReportCancel: 'Cancel',
    bugReportSubmit: 'Submit',
    bugReportSubmitting: 'Submitting...',
    bugReportSuccessTitle: 'Thank you for your feedback!',
    bugReportSuccessBody: 'We appreciate your help in making this app better.',
    bugReportError: 'Failed to submit bug report. Please try again.',
    bugReportLoginRequired: 'You must be logged in to submit a bug report.',
    bugReportClose: 'Close',

    // Landing Page
    aiPoweredReceiptManager: 'AI-powered receipt manager',
    aiPoweredFinanceApp: 'AI-powered personal finance',
    turnReceiptsIntoInsights: 'Turn receipts into insights',
    takeControlOfYourFinances: 'Take control of your spending and debt',
    landingPageSubtitle: 'Scan receipts, ingest credit card statements, track installments, and see where your money goes — all in one private, modular app.',
    tryTheApp: 'Try the App',
    seeFeatures: 'See features',
    noCreditCard: 'No credit card required. Free to get started.',
    screenshotTip: 'Tip: replace these visuals with real screenshots by adding files under public/images and updating src paths.',
    quickUploads: 'Quick uploads',
    quickUploadsSubtitle: 'Drag & drop receipts or import photos. We parse stores, dates, items, and totals automatically.',
    moduleReceiptsTitle: 'Receipts',
    moduleReceiptsSubtitle: 'JPG, PNG, PDF',
    moduleReceiptsLong: 'Snap receipts and let the AI extract stores, items, prices, and totals automatically.',
    moduleBudgetTitle: 'Credit cards',
    moduleBudgetSubtitle: 'Statements, installments',
    moduleBudgetLong: 'Import Turkish credit card statements, auto-categorize transactions, and forecast installment debt.',
    insightsAndReports: 'Insights & reports',
    insightsAndReportsSubtitle: 'Monitor monthly spend, category breakdowns, debt forecasts, and trends in one place.',
    priceTracking: 'Price tracking',
    priceTrackingSubtitle: 'Compare product prices over time and spot trends to optimize your budget.',
    privacyFirst: 'Privacy first',
    privacyFirstSubtitle: 'Your data stays secure. Log in to access your dashboard; log out to keep things private.',
    startNow: 'Start now',

    // Landing Page - How it works
    howItWorks: 'How it works',
    stepUpload: 'Upload',
    stepUploadDesc: 'Snap a photo of your receipt or drag & drop the image file.',
    stepAiProcesses: 'AI processes',
    stepAiProcessesDesc: 'Our AI reads and extracts every item, price, and store automatically.',
    stepViewInsights: 'View insights',
    stepViewInsightsDesc: 'See spending trends, price comparisons, and detailed reports instantly.',
    readyToStart: 'Ready to take control of your spending?',
    readyToStartSubtitle: 'Join now and start turning your receipts into actionable insights.',
    getStartedFree: 'Get started free',

    // Report detail labels
    detailedStats: 'Detailed Statistics',
    totalQuantity: 'Total Quantity',
    averageCost: 'Average Cost',
    lastVisit: 'Last Visit',
    storeName: 'Store',
    productName: 'Product Name',
    period: 'Period',
    totalSpendingAmount: 'Total Spending',
    receiptsCount: 'Receipts',
    tax: 'Tax',
    receiptWord: 'receipt',
    receiptsWord: 'receipts',
    noDataYetDescription: 'Upload your first receipt to see stats here.',

    // Month names
    monthJanuary: 'January',
    monthFebruary: 'February',
    monthMarch: 'March',
    monthApril: 'April',
    monthMay: 'May',
    monthJune: 'June',
    monthJuly: 'July',
    monthAugust: 'August',
    monthSeptember: 'September',
    monthOctober: 'October',
    monthNovember: 'November',
    monthDecember: 'December',

    // Receipt history summary
    totalSpendingSummary: 'Total Spending',
    totalReceiptsSummary: 'Total Receipts',

    reportBugButton: 'Report Bug',
    back: 'Back',
    saveChanges: 'Save Changes',
    selectDate: 'Select Date',
    clear: 'Clear',
    noRecentStores: 'No recent stores',

    // DataTable / nav
    search: 'Search...',
    collapseAll: 'Collapse all',
    page: 'Page',
    of: 'of',
    show: 'Show',
    somethingWentWrong: 'Something went wrong.',

    // Sharing
    share: 'Share',
    shareLink: 'Shared',
    shareReceipt: 'Share Receipt',
    copyLink: 'Copy Link',
    linkCopied: 'Copied!',
    unshare: 'Unshare',
    receiptNotFound: 'Receipt not found or link has expired.',
    sharedReceipt: 'Shared Receipt',

    // Login
    loggingIn: 'Logging in...',
    or: 'or',
    continueWithGoogle: 'Continue with Google',
    dontHaveAnAccount: "Don't have an account?",
    loginFeatureReceipts: 'Scan receipts, track every item',
    loginFeatureBudget: 'Import card statements, manage debt',
    loginFeatureInsights: 'Insights, reports, and forecasts',

    // Family module
    'module.family': 'Family',
    'family.overview': 'Overview',
    'family.receipts': 'Receipts',
    'family.cards': 'Cards',
    'family.statements': 'Statements',
    'family.forecast': 'Forecast',
    'family.settings': 'Settings',
    'family.create.title': 'Create a Family',
    'family.create.namePlaceholder': 'Family name (optional)',
    'family.create.submit': 'Create Family',
    'family.members.title': 'Members',
    'family.members.role.owner': 'Owner',
    'family.members.role.member': 'Member',
    'family.invite.create': 'Create Invite Link',
    'family.invite.created': 'Invite link copied to clipboard!',
    'family.invite.expires.never': 'Never',
    'family.invite.expires.in': 'Expires in (hours)',
    'family.invite.maxUses.none': 'Max uses (unlimited)',
    'family.invite.urlCopied': 'Invite link created — copy and share it:',
    'family.invite.delete': 'Delete',
    'family.invite.confirmDelete': 'Permanently delete this inactive invitation? Family members will not be affected.',
    'family.invite.deleteError': 'Could not delete the invitation. Please try again.',
    'family.invite.token': 'Token',
    'family.invite.status': 'Status',
    'family.invite.expires': 'Expires',
    'family.invite.uses': 'Uses',
    'family.invite.active': 'Active',
    'family.invite.inactive': 'Inactive',
    'family.invite.revoke': 'Revoke',
    'family.invite.confirmRevoke': 'Revoke this invitation? Active uses will be blocked.',
    'family.invite.invalid': 'This invite link is no longer valid.',
    'family.invite.expired': 'This invite link has expired.',
    'family.invite.alreadyMember': "You're already in this family.",
    'family.invite.inAnotherFamily': "You're in another family. Leave it first to join this one.",
    'family.share.receipts': 'Share Receipts',
    'family.share.budget': 'Share Budget',
    'family.share.tooltip': 'Sharing Settings',
    'family.leave': 'Leave Family',
    'family.leave.confirm': 'Leave this family? Your data will no longer be visible to other members.',
    'family.disband': 'Disband Family',
    'family.disband.confirm': 'Disband this family? All members will lose shared visibility.',
    'family.remove': 'Remove',
    'family.remove.confirm': 'Remove this member from the family?',
    'family.reportDebtForecast': 'Family Debt Forecast',
    'family.empty.title': 'No family yet',
    'family.empty.cta': 'Create a family to share visibility with household members.',
    'family.widget.thisMonthSpending': 'This month\'s spending',
    'family.widget.nextMonthDebt': 'Next month installment debt',

    // Privacy policy (/privacy). Keep it in step with what the code does.
    'privacy.link': 'Privacy policy',
    'privacy.title': 'Privacy policy',
    'privacy.lastUpdated': 'Last updated: 22 October 2026',
    'privacy.language': 'Language',
    'privacy.backHome': 'Back to home',
    'privacy.intro': 'Mizan is a personal finance app: you upload receipts and credit card statements, and it turns them into spending reports. This page explains what data the app handles, who else sees it, and how to delete it. It covers the web app at fis4j.okankonur.com and the Mizan iOS app.',
    'privacy.operator.title': 'Who runs Mizan',
    'privacy.operator.p1': 'Mizan is a personal project run by an individual developer, not a company. The same person operates the server and is responsible for your data. You can reach them at the contact address at the end of this page.',
    'privacy.collected.title': 'What is collected',
    'privacy.collected.p1': 'Only what you give the app, and what it needs to sign you in:',
    'privacy.collected.item1': 'Account: your username, your email address, a hashed password if you sign up with one (never the password itself), the account identifier Google or Apple gives us if you sign in with them, and when the account was created.',
    'privacy.collected.item2': 'Receipts: the photos you upload and what is read from them: store, date, receipt number, items, prices, discounts and totals.',
    'privacy.collected.item3': 'Credit cards and statements: the cards you add (bank, card name, last four digits, limit), the text read from each statement PDF, which includes whatever the bank prints on it, such as your name, and its transactions, totals, due dates and installments. The PDF file itself is not kept, only its text and a fingerprint used to spot duplicate uploads.',
    'privacy.collected.item4': 'Your spending categories, categorization rules, and the links between receipts and card transactions.',
    'privacy.collected.item5': 'Family: the family groups you create or join, their members, invitations, and your sharing settings.',
    'privacy.collected.item6': 'Bug reports you send: your description, any contact details you choose to add, and your browser or device and app version.',
    'privacy.collected.item7': 'Technical logs: the IP address, time and requested address of calls to the server, and security events such as sign-ins, failed sign-ins and account deletion. These are used to run and protect the service.',
    'privacy.ai.title': 'AI processing by a third party',
    'privacy.ai.p1': 'Receipts and statements are read by an AI service from an outside provider. By default this is Mistral AI (France, EU). The operator can switch the server to OpenAI or Google Gemini, both based in the United States; your documents would then be processed outside the EU, and this page will be updated before that happens.',
    'privacy.ai.p2': 'What the provider receives:',
    'privacy.ai.item1': 'For a receipt: the photo you uploaded, with instructions describing what to extract.',
    'privacy.ai.item2': "For a statement: with Mistral, the PDF file, read with Mistral's document OCR; with the other providers, the text extracted from the PDF on our server. When an amount cannot be confirmed, a second request sends the statement text and the lines to check again.",
    'privacy.ai.item3': 'Your username, email address and password are never sent. The documents themselves can contain personal details, such as your name or the last digits of your card, and those reach the provider as part of the document.',
    'privacy.ai.note': "The provider sends the extracted data back to our server, where it is stored. It handles each request under its own API terms and privacy policy, which set how long it keeps requests and what it may use them for. Your data is not sent to the provider for any other purpose. Only upload documents you are comfortable having processed this way.",
    'privacy.storage.title': 'Where your data is stored',
    'privacy.storage.p1': "Your data is stored in a database on a server rented from Hetzner Online in a data centre in the European Union. Connections to the server are encrypted with HTTPS. The operator keeps backups of the database, which are replaced over time.",
    'privacy.storage.p2': 'Your data is not sold, rented or shared with anyone other than the services named on this page.',
    'privacy.signin.title': 'Signing in with Google or Apple',
    'privacy.signin.p1': 'If you sign in with Google or Apple, we receive your email address and an account identifier from them and use these only to create your Mizan account and sign you in. Apple lets you hide your email, in which case we receive an Apple relay address instead. We get no access to anything else in your Google or Apple account.',
    'privacy.tracking.title': 'No tracking, no ads',
    'privacy.tracking.p1': 'Mizan shows no ads, uses no analytics or tracking tools, and does not track you across other apps or websites. The web app stores only your sign-in token and display preferences (such as the theme) in your browser; the iOS app keeps its sign-in tokens in the iOS Keychain. Signing in with Google on the web uses a short-lived session cookie for the sign-in itself.',
    'privacy.family.title': 'Family sharing',
    'privacy.family.p1': 'If you join a family group, the other members can see what you share with them: your receipts, and your cards, statements, transactions and installment forecast. Each member turns receipt sharing and budget sharing on or off for themselves; both are on when you join. Leaving the family, or the owner disbanding it, ends the shared visibility.',
    'privacy.sharing.title': 'Public receipt links',
    'privacy.sharing.p1': "You can create a share link for a single receipt. Anyone who has the link can see that receipt's details and photo without signing in, until you stop sharing it. Only send links to people you trust.",
    'privacy.deletion.title': 'Deleting your account',
    'privacy.deletion.p1': 'You can delete your account in the iOS app under Settings → Delete account. If you only use the web app, email the contact address below and the operator will delete it for you. Deletion cannot be undone.',
    'privacy.deletion.p2': 'Deleting your account removes:',
    'privacy.deletion.item1': 'your receipts with their photos, items, discounts and share links;',
    'privacy.deletion.item2': 'your cards, statements, transactions, categories, rules, installments and receipt links;',
    'privacy.deletion.item3': 'your family membership (if you own a family, it is disbanded for all members);',
    'privacy.deletion.item4': 'your bug reports and sign-in sessions;',
    'privacy.deletion.item5': 'your account itself.',
    'privacy.deletion.note': "What is kept: the security log of account events, which records your user id and username (for example, that the account was deleted), and the shared list of store and product names built from everyone's receipts, which is not linked to you. Receipt photos are cleared from storage at the next database clean-up, and backups made before the deletion may contain your data until they are replaced.",
    'privacy.rights.title': 'Your rights',
    'privacy.rights.p1': "Depending on where you live (for example under the EU GDPR or Turkey's KVKK), you can ask to see the data held about you, have it corrected, receive a copy, or have it deleted. Email the contact address below. You can also complain to your data protection authority.",
    'privacy.children.title': 'Children',
    'privacy.children.p1': 'Mizan is not intended for children under 16.',
    'privacy.changes.title': 'Changes to this policy',
    'privacy.changes.p1': 'If this policy changes, the new version is published on this page with a new "Last updated" date.',
    'privacy.contact.title': 'Contact',
    'privacy.contact.p1': 'Questions or requests about your data:',
    'privacy.contactEmail': 'okan.konur@gmail.com',
  },
  tr: {
    // Brand
    appName: 'Mizan',
    appTagline: 'Modüler kişisel finans.',

    // Navigation
    dashboard: 'Ana Sayfa',
    reports: 'Raporlar',
    receiptHistory: 'Fiş Geçmişi',
    uploadReceipt: 'Fiş Yükle',
    login: 'Giriş',
    signup: 'Kayıt Ol',
    logout: 'Çıkış',
    greeting: 'Merhaba, {{name}}',
    openMenu: 'Menüyü aç',
    closeMenu: 'Menüyü kapat',
    apiKey: 'API Anahtarı',

    // Dashboard empty state
    dashboardEmptyTitle: 'Etkin modül yok',
    dashboardEmptyDescription: 'Burada widget görmek için ayarlardan bir modül etkinleştirin.',

    // Onboarding modal
    onboardingTitle: 'Mizan\'a hoş geldiniz',
    onboardingIntro: 'Fiş takibi modüler bir kişisel finans uygulamasına dönüştü. Fişler hâlâ burada — ve dahası var.',
    onboardingReceiptsDesc: 'Fişleri yükleyin, ürünleri otomatik çıkarın ve harcamayı analiz edin — eskiden olduğu gibi.',
    onboardingBudgetDesc: 'Türk kredi kartı ekstrelerini içe aktarın, işlemleri otomatik kategorilendirin ve taksit borcunu takip edin.',
    onboardingClosingNote: 'Başlamak için navbar\'dan bir modül seçin. Bu mesajı bir daha görmeyeceksiniz.',
    onboardingDismiss: 'Anladım',

    // Common actions
    viewAll: 'Tümünü gör',
    insights: 'Analizler',
    apiSection: 'API',

    // API Key page
    'api.quickStart': 'Hızlı Başlangıç',
    'api.baseUrl': 'Temel URL',
    'api.authentication': 'Kimlik Doğrulama',
    'api.authDescPre': 'Her isteğe',
    'api.authDescPost': 'başlığını ekleyin.',
    'api.endpoints': 'Uç Noktalar',
    'api.colMethod': 'Yöntem',
    'api.colPath': 'Yol',
    'api.colDesc': 'Açıklama',
    'api.uploadCurl': 'Fiş yükleme (curl)',
    'api.epListReceipts': 'Tüm fişleri listele (sayfalandırılmış)',
    'api.epGetReceipt': 'Tek bir fişi getir',
    'api.epGetItems': 'Fişin satır kalemlerini getir',
    'api.epProcess': 'Fiş görüntüsünü yükle ve yapay zekayla işle (çok parçalı form, alan: file)',
    'api.epDashStats': 'Panoda kullanılan harcama istatistikleri',
    'api.iosShortcuts': 'iOS Kısayolları',
    'api.iosIntro': 'iPhone\'daki Kısayollar uygulamasıyla fiş yüklemelerini otomatikleştirin ya da tek dokunuşla veya Siri komutuyla harcama verilerinize ulaşın.',
    'api.keyLabel': 'Anahtar:',
    'api.valueLabel': 'Değer: anahtarınız',
    'api.r1Title': 'Son fişleri getir',
    'api.r1Step1': 'Kısayollar\'ı açın → + → Eylem Ekle',
    'api.r1Step2': '"URL İçeriklerini Al" eylemini aratın ve ekleyin',
    'api.r1Step3': 'URL\'yi şuna ayarlayın:',
    'api.r1Step4': 'Üstbilgiler\'i genişletin → Üstbilgi ekle:',
    'api.r1Step5': '"Giriş\'ten Sözlük Al" ekleyin (isteğe bağlı — belirli alanlar için)',
    'api.r1Step6': '"Sonucu Göster" veya "Hızlı Bak" ekleyerek yanıtı önizleyin',
    'api.r1Note': 'İpucu: kısayolu "Fişlerim" olarak adlandırın ve "Hey Siri, Fişlerim" ile tetikleyin.',
    'api.r2Title': 'Fiş fotoğrafı yükle',
    'api.r2Step1': '"Fotoğrafları Seç" eylemini ekleyin (1 fotoğrafla sınırlayın)',
    'api.r2Step2': '"URL İçeriklerini Al" ekleyin',
    'api.r2Step3': 'URL\'yi şuna ayarlayın:',
    'api.r2Step4Pre': 'Yöntem\'i şuna ayarlayın:',
    'api.r2Step5a': 'İstek Gövdesi → Form olarak ayarlayın',
    'api.r2Step5bPre': 'Alan ekleyin: Anahtar',
    'api.r2Step5bPost': ', Değer → Fotoğraflar (1. adımdan değişken)',
    'api.r2Step6': 'Üstbilgi ekleyin:',
    'api.r2Step6b': ', Değer: anahtarınız',
    'api.r2Step7': '"Sonucu Göster" ekleyin — yapay zekanın çıkardığı fiş verisi anında görünür',
    'api.r2Note': 'İpucu: alışverişten sonra tek dokunuşla fiş almak için bu kısayolu ana ekranınıza ekleyin.',

    // Modules
    'module.receipts': 'Fişler',
    'module.budget': 'Bütçe',

    // Budget module
    'budget.statements': 'Ekstreler',
    'budget.statement': 'Ekstre',
    'budget.uploadStatement': 'Ekstre Yükle',
    'budget.selectPdf': 'PDF seçin',
    'budget.pdfHint': 'Kredi kartı ekstresinin PDF\'sini yükleyin. En fazla 10MB.',
    'budget.processing': 'İşleniyor...',
    'budget.uploadSuccess': '{{bank}} bankasından {{count}} işlem içe aktarıldı.',
    'budget.uploadError': 'Ekstre yüklenemedi.',
    'budget.uploadErrorPdfOnly': 'Sadece PDF dosyaları destekleniyor.',
    'budget.uploadErrorTooLarge': 'PDF 10 MB\'ı aşıyor.',
    'budget.uploadErrorDuplicate': 'Bu ekstre daha önce yüklenmiş.',
    'budget.uploadErrorParse': 'Bu ekstre ayrıştırılamadı.',
    'budget.uploadErrorParse.PDF_UNREADABLE': 'Dosya PDF olarak açılamadı. Bozuk veya şifre korumalı olabilir. Ekstreyi bankanızdan yeniden indirip tekrar deneyin.',
    'budget.uploadErrorParse.NO_TEXT': 'PDF içinde okunabilir metin yok. Taranmış veya yalnızca görüntüden oluşan ekstreler henüz desteklenmiyor. Bankanızın sitesinden ya da uygulamasından orijinal PDF ekstreyi indirin.',
    'budget.uploadErrorParse.LLM_RATE_LIMITED': 'Ayrıştırma servisi şu an yoğun (istek sınırına ulaşıldı). Dosyanızda bir sorun yok. Bir dakika bekleyip tekrar deneyin.',
    'budget.uploadErrorParse.LLM_UNAVAILABLE': 'Ayrıştırma servisi geçici olarak kullanılamıyor. Dosyanızda bir sorun yok. Birkaç dakika sonra tekrar deneyin.',
    'budget.uploadErrorParse.LLM_AUTH': 'Ayrıştırma servisi sunucu kimlik bilgilerini reddetti. Bu bir sunucu yapılandırma sorunudur. Lütfen bildirin.',
    'budget.uploadErrorParse.INVALID_RESPONSE': 'Ekstre metni okundu ancak içeriği işlemlere dönüştürülemedi. Bu bankanın ekstre düzeni henüz desteklenmiyor olabilir. Lütfen banka adıyla birlikte bildirin.',
    'budget.uploadErrorParse.UNKNOWN': 'Ayrıştırma sırasında beklenmeyen bir hata oluştu. Lütfen tekrar deneyin veya sorunu bildirin.',
    'budget.uploadErrorDetail': 'Teknik ayrıntı',
    'budget.viewStatement': 'Ekstreyi görüntüle',
    'budget.viewDetails': 'Detaylar',
    'budget.noStatementsYet': 'Henüz ekstre yüklenmedi.',
    'budget.period': 'Dönem',
    'budget.totalDue': 'Toplam borç',
    'budget.minimumPayment': 'Asgari ödeme',
    'budget.dueDate': 'Son ödeme',
    'budget.confirmDeleteStatement': 'Bu ekstreyi ve tüm işlemlerini silmek istiyor musunuz?',
    'budget.transactions': 'İşlemler',
    'budget.merchant': 'İş yeri',
    'budget.amount': 'Tutar',
    'budget.installment': 'Taksit',
    'budget.installments': 'Taksitler',
    'budget.category': 'Kategori',
    'budget.uncategorized': 'Kategorisiz',
    'budget.edited': 'Düzenlendi',
    'budget.amountNeedsReview': 'Tutarı kontrol edin',
    'budget.amountNeedsReviewHint': 'Bu tutar ekstreyle doğrulanamadı. PDF ile karşılaştırın.',
    'budget.amountsNeedReview': '{{count}} tutar ekstreyle doğrulanamadı. İşaretli satırları PDF ile karşılaştırıp düzeltin ya da onaylayın.',
    'budget.correctAmount': 'Düzelt',
    'budget.confirmAmount': 'Tutar doğru',
    'budget.amountInputLabel': 'Ekstrede yazan tutar',
    'budget.amountInvalid': 'Sıfırdan farklı, en fazla iki ondalıklı bir tutar girin; örn. 42.333,00',
    'budget.amountCorrected': 'Tutar güncellendi',
    'budget.amountConfirmed': 'Tutar onaylandı',
    'budget.amountUpdateFailed': 'Tutar güncellenemedi. Lütfen tekrar deneyin.',
    'budget.linkReceipt': 'Fişe bağla',
    'budget.noReceiptSuggestions': 'Yakın bir fiş adayı bulunamadı.',
    'budget.link': 'Bağla',

    'budget.cards': 'Kartlar',
    'budget.card': 'Kart',
    'budget.addCard': 'Kart ekle',
    'budget.bank': 'Banka',
    'budget.cardName': 'Kart adı',
    'budget.last4': 'Son 4',
    'budget.color': 'Renk',
    'budget.creditLimit': 'Kart limiti',
    'budget.add': 'Ekle',
    'budget.noCardsYet': 'Henüz kart eklenmedi.',
    'budget.confirmDeleteCard': 'Bu kartı silmek istiyor musunuz?',

    'budget.categoriesAndRules': 'Kategoriler ve kurallar',
    'budget.categories': 'Kategoriler',
    'budget.rules': 'Kurallar',
    'budget.addCategory': 'Kategori ekle',
    'budget.addRule': 'Kural ekle',
    'budget.displayNameTr': 'Adı (TR)',
    'budget.displayNameEn': 'Adı (EN)',
    'budget.confirmDeleteCategory': 'Bu kategoriyi silmek istiyor musunuz?',
    'budget.matchType': 'Eşleşme tipi',
    'budget.pattern': 'Desen',
    'budget.priority': 'Öncelik',
    'budget.pickCategory': 'Kategori seçin',
    'budget.system': 'Sistem',

    'budget.addInstallmentManual': 'Taksit ekle',
    'budget.pickCardOptional': 'Kart (opsiyonel)',
    'budget.totalAmount': 'Toplam tutar',
    'budget.monthlyAmount': 'Aylık',
    'budget.count': 'Adet',
    'budget.firstDueMonth': 'İlk taksit ayı',
    'budget.nextDue': 'Sonraki taksit',
    'budget.progress': 'İlerleme',
    'budget.open': 'Açık',
    'budget.completed': 'Tamamlanmış',
    'budget.all': 'Hepsi',
    'budget.markCompleted': 'Tamamlandı',
    'budget.reopen': 'Yeniden aç',
    'budget.confirmDeleteInstallment': 'Bu taksit planını silmek istiyor musunuz?',
    'budget.noInstallments': 'Taksit yok.',
    'budget.unconfirmedMonths': '{{count}} ay ekstreyle doğrulanmadı',
    'budget.totalDueForMonth': 'Toplam borç',
    'budget.monthlyContribution': 'Bu ay',
    'budget.gridView': 'Kart',
    'budget.listView': 'Liste',
    'budget.viewMode': 'Görünüm',

    'budget.findDuplicates': 'Tekrarları bul',
    'budget.duplicatesModalTitle': 'Tekrar eden taksitler',
    'budget.duplicatesScanning': 'Tekrarlar taranıyor…',
    'budget.duplicatesNoneFound': 'Tekrar eden taksit bulunamadı.',
    'budget.duplicatesFound': '{{count}} küme bulundu, {{rows}} satır kapsanıyor.',
    'budget.duplicatesWinner': 'Tut',
    'budget.duplicatesLoser': 'Birleştir ve sil',
    'budget.duplicatesReason': 'Tutulan kayıt seçimi:',
    'budget.duplicatesReasonNotes': 'manuel not içeriyor',
    'budget.duplicatesReasonProgress': 'en çok ödenmiş taksit',
    'budget.duplicatesReasonEarliest': 'en eski kayıt',
    'budget.duplicatesApply': 'Birleştirmeyi uygula',
    'budget.duplicatesApplying': 'Birleştiriliyor…',
    'budget.duplicatesConfirm': 'Bu işlem kaybeden satırları kalıcı olarak silecek ve işlemlerini kazanana yönlendirecek. Devam edilsin mi?',
    'budget.duplicatesAppliedSummary': '{{clusters}} küme birleştirildi: {{rows}} taksit silindi, {{txns}} işlem yönlendirildi.',

    'budget.reportDebtForecast': 'Borç projeksiyonu',
    'budget.sixMonths': '6 ay',
    'budget.twelveMonths': '12 ay',
    'budget.twentyFourMonths': '24 ay',
    'budget.clickToViewInstallments': 'Taksitleri görüntülemek için tıkla',
    'budget.allMonths': 'Tüm aylar',
    'budget.noInstallmentsForMonth': 'Bu ay taksit yok.',
    'budget.toastCompleted': 'Tamamlandı olarak işaretlendi',
    'budget.toastReopened': 'Taksit yeniden açıldı',
    'budget.toastDeleted': 'Taksit silindi',
    'budget.total': 'Toplam',
    'budget.shareOfTotal': 'Pay',

    'budget.dashboardTitle': 'Bütçe',
    'budget.widgetNextMonthDue': 'Gelecek ay taksit borcu',
    'budget.widgetOpenInstallments': 'Açık taksitler',

    'budget.widgetMonthSpend': 'Bu ay harcanan',
    'budget.widgetMonthSpendHint': 'Kart ve fişler, her alışveriş bir kez',

    'budget.reportSpendingByCategory': 'Kategoriye göre harcama',
    'budget.reportSpendingByStore': 'Mağazaya göre harcama',
    'budget.spendingTotal': 'Toplam harcama',
    'budget.spendingMonthlyAverage': 'Aylık ortalama {{amount}}',
    'budget.spendingByCard': 'Kartla ödenen',
    'budget.spendingCardHint': '{{count}} işlem, {{matched}} tanesi fişli',
    'budget.spendingReceiptOnly': 'Kart işlemi olmayan fişler',
    'budget.spendingReceiptOnlyHint': '{{count}} fiş: nakit, banka kartı ya da ekstresi henüz yüklenmemiş',
    'budget.spendingEmpty': 'Bu dönemde harcama yok. Başlamak için bir ekstre ya da fiş yükleyin.',
    'budget.spendingCategoryBreakdown': 'Kategoriler',
    'budget.spendingShare': 'Pay %{{percent}} · {{count}} alışveriş',
    'budget.spendingStoreMeta': '{{count}} alışveriş · {{receipts}} tanesi fişli · pay %{{percent}}',
    'budget.spendingSearchStores': 'Mağaza ara',

    'budget.receipt': 'Fiş',
    'budget.receiptLinked': 'Fiş bağlı',
    'budget.unlinkReceipt': 'Fiş bağlantısını kaldır',
    'budget.linkReceiptHint': 'Bu işlemin fişini seçin. Bağlı bir alışveriş raporlarda bir kez sayılır.',
    'budget.unknownStore': 'Bilinmeyen mağaza',
    'budget.matchScore': 'Eşleşme puanı',
    'budget.receiptsLinkedCount': '{{count}} tanesi fişli',
    'budget.matchReceipts': 'Fişleri eşleştir',
    'budget.matchingReceipts': 'Eşleştiriliyor…',
    'budget.matchReceiptsDone': '{{count}} fiş kart işlemine bağlandı.',
    'budget.matchReceiptsNone': 'Yeni eşleşme bulunamadı. Fişleri elle bağlayabilirsiniz.',

    'receipts.payment': 'Ödeme',
    'receipts.card': 'Kart',
    'receipts.paidByCard': 'Kartla ödendi',
    'receipts.viewStatement': 'Ekstreyi gör',

    // Auth
    username: 'Kullanıcı Adı',
    email: 'E-posta',
    password: 'Şifre',
    confirmPassword: 'Şifre Tekrar',
    loginButton: 'Giriş Yap',
    signupButton: 'Kayıt Ol',
    
    // Dashboard
    welcomeMessage: 'Fiş Analiz Sistemine Hoşgeldiniz',
    totalSpending: 'Toplam Harcama',
    monthlyOverview: 'Aylık Genel Bakış',
    totalReceipts: 'Toplam Fişler',
    spendingLast30Days: 'Son 30 Günlük Harcama',
    totalAmount: 'Toplam Tutar',
    weeklySummary: 'Haftalık Özet',
    totalSpendingLast7Days: 'Toplam Harcama (Son 7 Gün)',
    receiptsUploadedLast7Days: 'Yüklenen Fişler (Son 7 Gün)',
    recentStores: 'Son Marketler',
    
    // Reports
    'reports.allReports': 'Tüm raporlar',
    'reports.selectReport': 'Rapor seçin',
    'reports.emptyTitle': 'Başlamak için bir rapor seçin',
    'reports.emptyDescription': 'Detayları görmek için listeden bir rapor seçin.',
    'reports.jumpToReports': 'Raporlara git',
    'scope.mine': 'Benim',
    'scope.family': 'Aile',
    'scope.label': 'Kapsam',
    viewDetails: 'Detayları Gör',
    receiptCount: 'Fiş Sayısı: {{count}}',
    reset: 'Reset',
    selectStartDate: 'Başlangıç Tarihi',
    selectEndDate: 'Bitiş Tarihi',
    backToGroups: 'Gruplara Geri Dön',
    accountantExport: 'Muhasebe Raporu',
    accountantExportDescription: 'Muhasebeci için aylık fiş verilerini Excel dosyası olarak indirin.',
    accountantExportButton: 'Excel İndir',
    accountantExportDownloading: 'İndiriliyor...',
    accountantExportError: 'Rapor indirilemedi. Lütfen tekrar deneyin.',
    selectYear: 'Yıl',
    selectMonth: 'Ay',

    // Receipt Upload
    selectFile: 'Dosya Seç',
    upload: 'Yükle',
    uploadSuccess: 'Fiş başarıyla yüklendi',
    uploadError: 'Fiş yüklenirken hata oluştu',
    processing: 'İşleniyor...',
    supportedFormats: 'Desteklenen formatlar: JPG, PNG, GIF',
    selectReceiptImage: 'Fiş Görseli Seç',
    errorHeading: 'Yükleme Hatası',
    errorFileTooLarge: 'Dosya çok büyük. İzin verilen maksimum boyut: {{maxSize}}',
    errorUnsupportedFormat: 'Desteklenmeyen dosya formatı. Lütfen sadece JPG, PNG veya PDF dosyaları kullanın.',
    errorDuplicateReceipt: 'Bu fiş daha önce yüklenmiş.',
    errorInvalidFormat: 'Fiş işlenemiyor. Lütfen görüntünün net ve okunabilir olduğundan emin olun.',
    errorProcessing: 'Fiş işlenirken bir hata oluştu.',
    errorProcessingHelp: 'Lütfen tekrar deneyin veya sorun devam ederse destek ekibiyle iletişime geçin.',
    'receipts.uploadError.LLM_RATE_LIMITED': 'Fiş okuma servisi şu an yoğun. Fotoğrafınızda bir sorun yok. Bir dakika bekleyip tekrar deneyin.',
    'receipts.uploadError.LLM_UNAVAILABLE': 'Fiş okuma servisi geçici olarak kullanılamıyor. Fotoğrafınızda bir sorun yok. Birkaç dakika sonra tekrar deneyin.',
    'receipts.uploadError.LLM_AUTH': 'Fiş okuma servisi sunucu kimlik bilgilerini reddetti. Bu bir sunucu yapılandırma sorunudur. Lütfen bildirin.',
    'receipts.retryUpload': 'Tekrar dene',
    
    // Receipt History
    date: 'Tarih',
    store: 'Market',
    total: 'Toplam',
    items: 'Ürünler',
    searchReceipts: 'Fişlerde ara...',
    purchasedProducts: 'Satın Alınan Ürünler',
    searchProducts: 'Ürünlerde ara...',
    actions: 'İşlemler',
    viewReceipt: 'Fişi Görüntüle',
    product: 'Ürün',
    quantity: 'Miktar',
    unitPrice: 'Birim Fiyat',
    price: 'Fiyat',
    totalPrice: 'Toplam Fiyat',
    receipt: 'Fiş',
    edit: 'Düzenle',
    save: 'Kaydet',
    cancel: 'İptal',
    delete: 'Sil',
    deleting: 'Siliniyor',
    confirmDeletion: 'Silmeyi Onayla',
    confirmDeleteMessage: 'Bu fişi silmek istediğinizden emin misiniz?',
    actionCannotBeUndone: 'Bu işlem geri alınamaz.',
    deleteSuccess: 'Fiş başarıyla silindi.',
    deleteError: 'Fiş silinemedi. Lütfen tekrar deneyin.',

    // Common
    loading: 'Yükleniyor...',
    error: 'Bir hata oluştu',
    noData: 'Veri bulunamadı',
    
    // API Key Management
    apiKeyManagement: 'API Anahtarı Yönetimi',
    yourApiKey: 'API Anahtarınız',
    keepSecure: 'Bu anahtarı güvende tutun. Başkalarıyla paylaşmayın.',
    noApiKey: 'API anahtarı bulunamadı',
    copy: 'Kopyala',
    apiKeyUsage: 'Bu API anahtarı, API uç noktalarımıza yapılan istekleri doğrulamak için kullanılabilir. İsteklerinizin X-API-Key başlığına ekleyin.',
    fetchError: 'API anahtarı alınamadı. Lütfen daha sonra tekrar deneyin.',

    // Bug Report Modal
    bugReportTitle: '🐞 Hata Bildir',
    bugReportDescription: 'Açıklama',
    bugReportDescriptionPlaceholder: 'Lütfen hatayı açıklayın veya bir iyileştirme önerin.',
    bugReportContactInfo: 'İletişim Bilgisi',
    bugReportContactInfoPlaceholder: 'E-posta veya başka bir iletişim (isteğe bağlı, geri dönüş için)',
    bugReportExceptionDetails: 'İstisna Detayları',
    bugReportExceptionDetailsDev: '(geliştiriciler için)',
    bugReportBrowserInfo: 'Tarayıcı Bilgisi',
    bugReportBrowserInfoAuto: '(otomatik yakalandı)',
    bugReportCancel: 'İptal',
    bugReportSubmit: 'Gönder',
    bugReportSubmitting: 'Gönderiliyor...',
    bugReportSuccessTitle: 'Geri bildiriminiz için teşekkürler!',
    bugReportSuccessBody: 'Bu uygulamayı daha iyi hale getirmemize yardımcı olduğunuz için minnettarız.',
    bugReportError: 'Hata bildirimi gönderilemedi. Lütfen tekrar deneyin.',
    bugReportLoginRequired: 'Hata bildirimi göndermek için giriş yapmalısınız.',
    bugReportClose: 'Kapat',

    // Landing Page
    aiPoweredReceiptManager: 'Yapay zeka destekli fiş yöneticisi',
    aiPoweredFinanceApp: 'Yapay zeka destekli kişisel finans',
    turnReceiptsIntoInsights: 'Fişleri analizlere dönüştürün',
    takeControlOfYourFinances: 'Harcamalarınızın ve borçlarınızın kontrolünü elinize alın',
    landingPageSubtitle: 'Fişleri tarayın, kredi kartı ekstrelerini içe aktarın, taksitleri takip edin ve paranızın nereye gittiğini tek bir gizli, modüler uygulamada görün.',
    tryTheApp: 'Uygulamayı Dene',
    seeFeatures: 'Özellikleri Gör',
    noCreditCard: 'Kredi kartı gerekmez. Başlamak ücretsiz.',
    screenshotTip: 'İpucu: Bu görselleri public/images altına dosyalar ekleyerek ve src yollarını güncelleyerek gerçek ekran görüntüleriyle değiştirin.',
    quickUploads: 'Hızlı yüklemeler',
    quickUploadsSubtitle: 'Fişleri sürükleyip bırakın veya fotoğrafları içe aktarın. Mağazaları, tarihleri, ürünleri ve toplamları otomatik olarak ayrıştırıyoruz.',
    moduleReceiptsTitle: 'Fişler',
    moduleReceiptsSubtitle: 'JPG, PNG, PDF',
    moduleReceiptsLong: 'Fişlerinizin fotoğrafını çekin; yapay zeka mağazaları, ürünleri, fiyatları ve toplamları otomatik olarak çıkarsın.',
    moduleBudgetTitle: 'Kredi kartları',
    moduleBudgetSubtitle: 'Ekstreler, taksitler',
    moduleBudgetLong: 'Türk kredi kartı ekstrelerini içe aktarın, işlemleri otomatik kategorilendirin ve taksit borç projeksiyonu görün.',
    insightsAndReports: 'Analizler ve raporlar',
    insightsAndReportsSubtitle: 'Aylık harcamayı, kategori dağılımını, borç projeksiyonunu ve trendleri tek bir yerde izleyin.',
    priceTracking: 'Fiyat takibi',
    priceTrackingSubtitle: 'Bütçenizi optimize etmek için ürün fiyatlarını zaman içinde karşılaştırın ve trendleri tespit edin.',
    privacyFirst: 'Önce gizlilik',
    privacyFirstSubtitle: 'Verileriniz güvende kalır. Panonuza erişmek için giriş yapın; işleri gizli tutmak için çıkış yapın.',
    startNow: 'Şimdi başla',

    // Landing Page - How it works
    howItWorks: 'Nasıl çalışır',
    stepUpload: 'Yükle',
    stepUploadDesc: 'Fişinizin fotoğrafını çekin veya dosyayı sürükleyip bırakın.',
    stepAiProcesses: 'Yapay zeka işler',
    stepAiProcessesDesc: 'Yapay zekamız her ürünü, fiyatı ve marketi otomatik olarak okur ve çıkarır.',
    stepViewInsights: 'Analizleri görün',
    stepViewInsightsDesc: 'Harcama trendlerini, fiyat karşılaştırmalarını ve detaylı raporları anında görün.',
    readyToStart: 'Harcamalarınızı kontrol altına almaya hazır mısınız?',
    readyToStartSubtitle: 'Şimdi katılın ve fişlerinizi eyleme dönüştürülebilir analizlere çevirmeye başlayın.',
    getStartedFree: 'Ücretsiz başla',

    // Report detail labels
    detailedStats: 'Detaylı İstatistikler',
    totalQuantity: 'Toplam Miktar',
    averageCost: 'Ortalama Maliyet',
    lastVisit: 'Son Ziyaret',
    storeName: 'Market',
    productName: 'Ürün Adı',
    period: 'Dönem',
    totalSpendingAmount: 'Toplam Harcama Tutarı',
    receiptsCount: 'Fiş Adedi',
    tax: 'KDV',
    receiptWord: 'fiş',
    receiptsWord: 'fiş',
    noDataYetDescription: 'İstatistikleri görmek için ilk fişinizi yükleyin.',

    // Month names
    monthJanuary: 'Ocak',
    monthFebruary: 'Şubat',
    monthMarch: 'Mart',
    monthApril: 'Nisan',
    monthMay: 'Mayıs',
    monthJune: 'Haziran',
    monthJuly: 'Temmuz',
    monthAugust: 'Ağustos',
    monthSeptember: 'Eylül',
    monthOctober: 'Ekim',
    monthNovember: 'Kasım',
    monthDecember: 'Aralık',

    // Receipt history summary
    totalSpendingSummary: 'Toplam Harcama',
    totalReceiptsSummary: 'Toplam Fiş',

    reportBugButton: 'Hata Bildir',
    back: 'Geri',
    saveChanges: 'Değişiklikleri Kaydet',
    selectDate: 'Tarih Seç',
    clear: 'Temizle',
    noRecentStores: 'Yakın zamanda ziyaret edilen market yok',

    // DataTable / nav
    search: 'Ara...',
    collapseAll: 'Tümünü kapat',
    page: 'Sayfa',
    of: ' / ',
    show: 'Göster',
    somethingWentWrong: 'Bir şeyler ters gitti.',

    // Sharing
    share: 'Paylaş',
    shareLink: 'Paylaşıldı',
    shareReceipt: 'Fişi Paylaş',
    copyLink: 'Linki Kopyala',
    linkCopied: 'Kopyalandı!',
    unshare: 'Paylaşımı Kaldır',
    receiptNotFound: 'Fiş bulunamadı veya bağlantının süresi dolmuş.',
    sharedReceipt: 'Paylaşılan Fiş',

    // Login
    loggingIn: 'Giriş yapılıyor...',
    or: 'veya',
    continueWithGoogle: 'Google ile Devam Et',
    dontHaveAnAccount: 'Hesabınız yok mu?',
    loginFeatureReceipts: 'Fiş tara, her kalemi takip et',
    loginFeatureBudget: 'Kart ekstresi içe aktar, borcu yönet',
    loginFeatureInsights: 'Analiz, raporlar ve tahminler',

    // Family module
    'module.family': 'Aile',
    'family.overview': 'Genel Bakış',
    'family.receipts': 'Fişler',
    'family.cards': 'Kartlar',
    'family.statements': 'Ekstreler',
    'family.forecast': 'Projeksiyon',
    'family.settings': 'Ayarlar',
    'family.create.title': 'Aile Oluştur',
    'family.create.namePlaceholder': 'Aile adı (opsiyonel)',
    'family.create.submit': 'Aile Oluştur',
    'family.members.title': 'Üyeler',
    'family.members.role.owner': 'Sahip',
    'family.members.role.member': 'Üye',
    'family.invite.create': 'Davet Bağlantısı Oluştur',
    'family.invite.created': 'Davet bağlantısı panoya kopyalandı!',
    'family.invite.expires.never': 'Hiçbir zaman',
    'family.invite.expires.in': 'Süre (saat)',
    'family.invite.maxUses.none': 'Maksimum kullanım (sınırsız)',
    'family.invite.urlCopied': 'Davet bağlantısı oluşturuldu — kopyalayın ve paylaşın:',
    'family.invite.delete': 'Sil',
    'family.invite.confirmDelete': 'Bu pasif davet kalıcı olarak silinsin mi? Aile üyeleri etkilenmez.',
    'family.invite.deleteError': 'Davet silinemedi. Lütfen tekrar deneyin.',
    'family.invite.token': 'Davet Kodu',
    'family.invite.status': 'Durum',
    'family.invite.expires': 'Son Kullanma',
    'family.invite.uses': 'Kullanım',
    'family.invite.active': 'Aktif',
    'family.invite.inactive': 'Pasif',
    'family.invite.revoke': 'İptal Et',
    'family.invite.confirmRevoke': 'Bu daveti iptal etmek istiyor musunuz?',
    'family.invite.invalid': 'Bu davet bağlantısı artık geçerli değil.',
    'family.invite.expired': 'Bu davet bağlantısının süresi dolmuş.',
    'family.invite.alreadyMember': 'Bu ailenin zaten bir üyesisiniz.',
    'family.invite.inAnotherFamily': 'Başka bir ailedesiniz. Bu aileye katılmak için önce ayrılın.',
    'family.share.receipts': 'Fişleri Paylaş',
    'family.share.budget': 'Bütçeyi Paylaş',
    'family.share.tooltip': 'Paylaşım Ayarları',
    'family.leave': 'Aileden Ayrıl',
    'family.leave.confirm': 'Bu aileden ayrılmak istiyor musunuz? Verileriniz diğer üyeler tarafından artık görülemez.',
    'family.disband': 'Aileyi Dağıt',
    'family.disband.confirm': 'Bu aileyi dağıtmak istiyor musunuz? Tüm üyeler paylaşılan görünürlüğü kaybeder.',
    'family.remove': 'Çıkar',
    'family.remove.confirm': 'Bu üyeyi aileden çıkarmak istiyor musunuz?',
    'family.reportDebtForecast': 'Aile Borç Projeksiyonu',
    'family.empty.title': 'Henüz aile yok',
    'family.empty.cta': 'Ev üyeleriyle paylaşılan görünürlük için bir aile oluşturun.',
    'family.widget.thisMonthSpending': 'Bu ayki harcama',
    'family.widget.nextMonthDebt': 'Gelecek ay taksit borcu',

    // Gizlilik politikası (/privacy). Kodun yaptığıyla uyumlu tutun.
    'privacy.link': 'Gizlilik politikası',
    'privacy.title': 'Gizlilik politikası',
    'privacy.lastUpdated': 'Son güncelleme: 22 Ekim 2026',
    'privacy.language': 'Dil',
    'privacy.backHome': 'Ana sayfaya dön',
    'privacy.intro': 'Mizan bir kişisel finans uygulamasıdır: fişlerinizi ve kredi kartı ekstrelerinizi yüklersiniz, uygulama bunları harcama raporlarına dönüştürür. Bu sayfa, uygulamanın hangi verileri işlediğini, bunları başka kimlerin gördüğünü ve nasıl silebileceğinizi açıklar. fis4j.okankonur.com adresindeki web uygulamasını ve Mizan iOS uygulamasını kapsar.',
    'privacy.operator.title': "Mizan'ı kim işletiyor",
    'privacy.operator.p1': 'Mizan bir şirket değil, bireysel bir geliştiricinin yürüttüğü kişisel bir projedir. Sunucuyu işleten ve verilerinizden sorumlu olan aynı kişidir. Bu sayfanın sonundaki iletişim adresinden ulaşabilirsiniz.',
    'privacy.collected.title': 'Hangi veriler toplanır',
    'privacy.collected.p1': 'Yalnızca uygulamaya sizin verdikleriniz ve giriş yapmanız için gerekenler:',
    'privacy.collected.item1': 'Hesap: kullanıcı adınız, e-posta adresiniz, şifreyle kaydolduysanız şifrenizin özeti (şifrenin kendisi asla saklanmaz), Google veya Apple ile giriş yaptıysanız onların verdiği hesap kimliği ve hesabın oluşturulma zamanı.',
    'privacy.collected.item2': 'Fişler: yüklediğiniz fotoğraflar ve bunlardan okunanlar: mağaza, tarih, fiş numarası, ürünler, fiyatlar, indirimler ve toplamlar.',
    'privacy.collected.item3': 'Kredi kartları ve ekstreler: eklediğiniz kartlar (banka, kart adı, son dört hane, limit), her ekstre PDF’inden okunan metin (adınız gibi bankanın üzerine yazdığı her şey dahil) ve ekstrenin işlemleri, toplamları, son ödeme tarihleri ve taksitleri. PDF dosyasının kendisi saklanmaz; yalnızca metni ve aynı dosyanın tekrar yüklenmesini fark etmek için kullanılan bir parmak izi saklanır.',
    'privacy.collected.item4': 'Harcama kategorileriniz, kategorilendirme kurallarınız ve fişlerle kart işlemleri arasındaki eşleşmeler.',
    'privacy.collected.item5': 'Aile: oluşturduğunuz veya katıldığınız aile grupları, üyeleri, davetler ve paylaşım ayarlarınız.',
    'privacy.collected.item6': 'Gönderdiğiniz hata bildirimleri: açıklamanız, eklemeyi seçtiğiniz iletişim bilgileri ve tarayıcı veya cihaz ile uygulama sürümünüz.',
    'privacy.collected.item7': 'Teknik kayıtlar: sunucuya yapılan isteklerin IP adresi, zamanı ve istenen adresi; giriş, başarısız giriş ve hesap silme gibi güvenlik olayları. Bunlar hizmeti çalıştırmak ve korumak için kullanılır.',
    'privacy.ai.title': 'Üçüncü taraf yapay zekâ ile işleme',
    'privacy.ai.p1': 'Fişler ve ekstreler, dışarıdan bir sağlayıcının yapay zekâ hizmetiyle okunur. Varsayılan sağlayıcı Mistral AI’dır (Fransa, AB). İşletmeci sunucuyu, ikisi de Amerika Birleşik Devletleri merkezli OpenAI veya Google Gemini’ye geçirebilir; bu durumda belgeleriniz AB dışında işlenir ve bu sayfa geçişten önce güncellenir.',
    'privacy.ai.p2': 'Sağlayıcıya gönderilenler:',
    'privacy.ai.item1': 'Fiş için: yüklediğiniz fotoğraf ve nelerin çıkarılacağını anlatan talimatlar.',
    'privacy.ai.item2': 'Ekstre için: Mistral ile PDF dosyasının kendisi (Mistral’in belge OCR’ı ile okunur); diğer sağlayıcılarla, PDF’den sunucumuzda çıkarılan metin. Bir tutar doğrulanamazsa ikinci bir istekle ekstre metni ve yeniden kontrol edilecek satırlar gönderilir.',
    'privacy.ai.item3': 'Kullanıcı adınız, e-posta adresiniz ve şifreniz asla gönderilmez. Ancak belgelerin kendisi adınız veya kartınızın son haneleri gibi kişisel bilgiler içerebilir ve bunlar belgenin parçası olarak sağlayıcıya ulaşır.',
    'privacy.ai.note': 'Sağlayıcı çıkardığı verileri sunucumuza geri gönderir ve veriler orada saklanır. Sağlayıcı her isteği kendi API koşulları ve gizlilik politikası kapsamında işler; istekleri ne kadar süre tuttuğunu ve ne için kullanabileceğini bu koşullar belirler. Verileriniz sağlayıcıya başka hiçbir amaçla gönderilmez. Yalnızca bu şekilde işlenmesinden rahatsız olmayacağınız belgeleri yükleyin.',
    'privacy.storage.title': 'Verileriniz nerede saklanır',
    'privacy.storage.p1': 'Verileriniz, Hetzner Online’dan kiralanan ve Avrupa Birliği’ndeki bir veri merkezinde bulunan bir sunucudaki veritabanında saklanır. Sunucuyla bağlantılar HTTPS ile şifrelenir. İşletmeci veritabanının yedeklerini tutar; yedekler zamanla yenileriyle değiştirilir.',
    'privacy.storage.p2': 'Verileriniz satılmaz, kiralanmaz ve bu sayfada adı geçen hizmetler dışında kimseyle paylaşılmaz.',
    'privacy.signin.title': 'Google veya Apple ile giriş',
    'privacy.signin.p1': 'Google veya Apple ile giriş yaparsanız onlardan e-posta adresinizi ve bir hesap kimliği alırız ve bunları yalnızca Mizan hesabınızı oluşturmak ve giriş yapmanızı sağlamak için kullanırız. Apple e-postanızı gizlemenize izin verir; bu durumda bir Apple yönlendirme adresi alırız. Google veya Apple hesabınızdaki başka hiçbir şeye erişimimiz olmaz.',
    'privacy.tracking.title': 'Takip yok, reklam yok',
    'privacy.tracking.p1': 'Mizan reklam göstermez, analiz veya takip aracı kullanmaz ve sizi başka uygulama ya da web sitelerinde takip etmez. Web uygulaması tarayıcınızda yalnızca giriş belirtecinizi ve görünüm tercihlerinizi (tema gibi) saklar; iOS uygulaması giriş belirteçlerini iOS Anahtar Zinciri’nde tutar. Web’de Google ile girişte, yalnızca giriş işlemi için kısa ömürlü bir oturum çerezi kullanılır.',
    'privacy.family.title': 'Aile paylaşımı',
    'privacy.family.p1': 'Bir aile grubuna katılırsanız diğer üyeler onlarla paylaştıklarınızı görebilir: fişleriniz ile kartlarınız, ekstreleriniz, işlemleriniz ve taksit projeksiyonunuz. Her üye fiş paylaşımını ve bütçe paylaşımını kendisi açar veya kapatır; katıldığınızda ikisi de açıktır. Aileden ayrılmak veya ailenin sahibi tarafından dağıtılması paylaşılan görünürlüğü sona erdirir.',
    'privacy.sharing.title': 'Herkese açık fiş bağlantıları',
    'privacy.sharing.p1': 'Tek bir fiş için paylaşım bağlantısı oluşturabilirsiniz. Bağlantıya sahip olan herkes, siz paylaşımı durdurana kadar o fişin ayrıntılarını ve fotoğrafını giriş yapmadan görebilir. Bağlantıları yalnızca güvendiğiniz kişilere gönderin.',
    'privacy.deletion.title': 'Hesabınızı silme',
    'privacy.deletion.p1': 'Hesabınızı iOS uygulamasında Ayarlar → Hesabı sil bölümünden silebilirsiniz. Yalnızca web uygulamasını kullanıyorsanız aşağıdaki iletişim adresine e-posta gönderin; işletmeci hesabınızı sizin için siler. Silme işlemi geri alınamaz.',
    'privacy.deletion.p2': 'Hesabınızı silmek şunları kaldırır:',
    'privacy.deletion.item1': 'fişleriniz ile fotoğrafları, ürünleri, indirimleri ve paylaşım bağlantıları;',
    'privacy.deletion.item2': 'kartlarınız, ekstreleriniz, işlemleriniz, kategorileriniz, kurallarınız, taksitleriniz ve fiş eşleşmeleriniz;',
    'privacy.deletion.item3': 'aile üyeliğiniz (bir ailenin sahibiyseniz aile tüm üyeler için dağıtılır);',
    'privacy.deletion.item4': 'hata bildirimleriniz ve giriş oturumlarınız;',
    'privacy.deletion.item5': 'hesabınızın kendisi.',
    'privacy.deletion.note': 'Saklananlar: kullanıcı kimliğinizi ve kullanıcı adınızı içeren hesap olayları güvenlik kaydı (örneğin hesabın silindiği bilgisi) ve herkesin fişlerinden oluşan, sizinle ilişkilendirilmeyen ortak mağaza ve ürün adları listesi. Fiş fotoğrafları bir sonraki veritabanı temizliğinde depolamadan kaldırılır; silmeden önce alınmış yedekler, değiştirilene kadar verilerinizi içerebilir.',
    'privacy.rights.title': 'Haklarınız',
    'privacy.rights.p1': 'Yaşadığınız yere bağlı olarak (örneğin KVKK veya AB GDPR kapsamında) hakkınızda tutulan verileri görmeyi, düzeltilmesini, bir kopyasını almayı veya silinmesini isteyebilirsiniz. Aşağıdaki iletişim adresine e-posta gönderin. Ayrıca veri koruma otoritesine (Türkiye’de Kişisel Verileri Koruma Kurumu) şikâyette bulunabilirsiniz.',
    'privacy.children.title': 'Çocuklar',
    'privacy.children.p1': 'Mizan 16 yaşından küçük çocuklara yönelik değildir.',
    'privacy.changes.title': 'Bu politikadaki değişiklikler',
    'privacy.changes.p1': 'Bu politika değişirse yeni sürüm, yeni bir "Son güncelleme" tarihiyle bu sayfada yayınlanır.',
    'privacy.contact.title': 'İletişim',
    'privacy.contact.p1': 'Verilerinizle ilgili sorular veya talepler için:',
    'privacy.contactEmail': 'okan.konur@gmail.com',
  }
};

const TranslationContext = createContext();

export const TranslationProvider = ({ children, language: initialLanguage }) => {
  const [language, setLanguage] = useState(initialLanguage || 'tr'); // Default to Turkish

  const t = (key, options) => {
    let translation = translations[language][key] || key;
    if (options) {
      Object.keys(options).forEach(optionKey => {
        const regex = new RegExp(`{{${optionKey}}}`, 'g');
        translation = translation.replace(regex, options[optionKey]);
      });
    }
    return translation;
  };

  return (
    <TranslationContext.Provider value={{ t, language, setLanguage }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};